/* eslint-disable import/no-cycle */
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { saveAs } from 'file-saver';
import BaseStore from './base-stores/BaseStore';
import { RootStore } from './RootStore';
import {
  AccountService,
  BalanceSettlementService,
  BalanceResponse,
  BalanceSettlement,
  BalanceSettlementStatus,
  BalanceType,
  CreateBalanceSettlementRequest,
  ProcessBalanceSettlementRequest
} from '../swagger/api';

export default class BalanceStore extends BaseStore {
  balance: BalanceResponse | null = null;

  settlementStatus: BalanceSettlementStatus | null = null;

  balanceSettlementId: string | null = null;

  settledAmount: number | null = null;

  settledCurrency: string | null = null;

  settledBalanceType: string | null = null;

  isPolling: boolean = false;

  private pollingInterval: NodeJS.Timeout | null = null;

  get shouldShowBalanceBanner(): boolean {
    if (!this.balance) return false;
    return this.balance.isSettlementRequired === true;
  }

  getBalance = (accountId: string): Promise<BalanceResponse | null> =>
    AccountService.getFinancialBalanceForCustomer({ accountId }).then(
      (response: BalanceResponse) => {
        const hasError = 'errorCode' in response && response.errorCode;
        const balance = hasError ? null : response;
        runInAction(() => {
          this.balance = balance;
        });
        return balance;
      }
    );

  hasCreditBalance = async (accountId: string): Promise<boolean> => {
    const balance = await this.getBalance(accountId);
    const balanceType = balance?.balanceType;
    return balanceType === BalanceType.Negative && balance?.isSettlementRequired === true;
  };

  // eslint-disable-next-line class-methods-use-this
  createBalanceSettlement = async (
    accountId: string,
    totalBalance: number
  ): Promise<{ sessionUrl?: string }> => {
    const body: CreateBalanceSettlementRequest = { totalBalance };
    const response = await BalanceSettlementService.createBalanceSettlementAsync({
      accountId,
      body
    });
    const apiResponse = response as {
      url?: string;
      sessionId?: string;
      balanceSettlementId?: string;
    };
    const sessionUrl =
      apiResponse?.url ??
      apiResponse?.sessionId ??
      (response as BalanceSettlement)?.paymentProviderSessionId;
    const balanceSettlementId =
      apiResponse?.balanceSettlementId ?? (response as BalanceSettlement)?.id;
    runInAction(() => {
      this.balanceSettlementId = balanceSettlementId ?? null;
    });
    return { sessionUrl };
  };

  // eslint-disable-next-line class-methods-use-this
  processBalanceSettlement = (
    accountId: string,
    body?: ProcessBalanceSettlementRequest
  ): Promise<unknown> => {
    const { balanceSettlementId } = this;
    if (!balanceSettlementId) {
      return Promise.reject(new Error('No active balance settlement'));
    }
    // Strip undefined values so we never send paymentMethodId: undefined
    const cleanedBody = body
      ? (Object.fromEntries(
          Object.entries(body).filter(([, v]) => v !== undefined)
        ) as ProcessBalanceSettlementRequest)
      : undefined;
    return BalanceSettlementService.processBalanceSettlementByIdAsync({
      accountId,
      balanceSettlementId,
      body: cleanedBody
    }).then((response) => response);
  };

  cancelBalanceSettlement = (accountId: string): Promise<unknown> => {
    const { balanceSettlementId } = this;
    if (!balanceSettlementId) {
      return Promise.reject(new Error('No active balance settlement'));
    }
    const promise = BalanceSettlementService.cancelBalanceSettlementByIdAsync({
      accountId,
      balanceSettlementId
    });
    promise.then(() => {
      runInAction(() => {
        this.balanceSettlementId = null;
      });
    });
    return promise;
  };

  getBalanceSettlementStatus = (accountId: string): Promise<BalanceSettlementStatus> => {
    const { balanceSettlementId } = this;
    if (!balanceSettlementId) {
      return Promise.reject(new Error('No active balance settlement'));
    }
    const promise = BalanceSettlementService.getBalanceSettlementStatusByIdAsync({
      accountId,
      balanceSettlementId
    });
    promise.then((status: BalanceSettlementStatus) => {
      runInAction(() => {
        this.settlementStatus = status;
      });
    });
    return promise;
  };

  pollSettlementStatus = async (accountId: string): Promise<'success' | 'failed'> => {
    this.isPolling = true;
    const startTime = Date.now();
    const MAX_POLL_DURATION_MS = 120_000;
    const FAST_INTERVAL_MS = 1_000;
    const SLOW_INTERVAL_MS = 3_000;
    const FAST_POLL_COUNT = 10;

    return new Promise((resolve, reject) => {
      let pollCount = 0;

      const poll = async () => {
        if (!this.isPolling) {
          return;
        }

        if (Date.now() - startTime >= MAX_POLL_DURATION_MS) {
          this.stopPolling();
          reject(new Error('Settlement polling timed out'));
          return;
        }

        try {
          pollCount += 1;
          const status = await this.getBalanceSettlementStatus(accountId);

          if (status.status === 'Settled' || status.status === 'PartiallySettled') {
            this.stopPolling();
            resolve('success');
            return;
          }

          if (status.status === 'Failed' || status.status === 'Cancelled') {
            this.stopPolling();
            runInAction(() => {
              this.balanceSettlementId = null;
            });
            reject(new Error(`Settlement ${status.status?.toLowerCase() ?? 'failed'}`));
            return;
          }

          if (this.isPolling) {
            const interval = pollCount < FAST_POLL_COUNT ? FAST_INTERVAL_MS : SLOW_INTERVAL_MS;
            this.pollingInterval = setTimeout(poll, interval);
          }
        } catch (error) {
          this.stopPolling();
          runInAction(() => {
            this.balanceSettlementId = null;
          });
          reject(error);
        }
      };

      this.pollingInterval = setTimeout(poll, FAST_INTERVAL_MS);
    });
  };

  stopPolling = () => {
    this.isPolling = false;
    if (this.pollingInterval) {
      clearTimeout(this.pollingInterval);
      this.pollingInterval = null;
    }
  };

  clearSettlement = () => {
    this.settledAmount = null;
    this.settledCurrency = null;
    this.settledBalanceType = null;
    this.settlementStatus = null;
    this.balanceSettlementId = null;
  };

  initiateSettlement = async (
    accountId: string,
    balance: BalanceResponse
  ): Promise<{ requiresReload?: boolean; sessionUrl?: string }> => {
    runInAction(() => {
      this.settledAmount = balance.totalBalance ?? 0;
      this.settledCurrency = balance.currency ?? null;
      this.settledBalanceType = balance.balanceType ?? null;
    });

    const isZeroBalance =
      balance.balanceType === BalanceType.Zero && balance.isSettlementRequired === true;

    if (isZeroBalance) {
      await this.createBalanceSettlement(accountId, 0);
      await this.processBalanceSettlement(accountId, { test: false });
      await this.pollSettlementStatus(accountId);
      return { requiresReload: true };
    }

    const result = await this.createBalanceSettlement(accountId, balance.totalBalance ?? 0);
    return { sessionUrl: result.sessionUrl };
  };

  // eslint-disable-next-line class-methods-use-this
  getBalanceSettlement = (accountId: string): Promise<BalanceSettlement> => {
    const { balanceSettlementId } = this;
    if (!balanceSettlementId) {
      return Promise.reject(new Error('No active balance settlement'));
    }
    return BalanceSettlementService.getBalanceSettlementByIdAsync({
      accountId,
      balanceSettlementId
    });
  };

  getBalanceSettlementPdf = (accountId: string) => {
    const { balanceSettlementId } = this;
    if (!balanceSettlementId) {
      return Promise.reject(new Error('No active balance settlement'));
    }
    const promise = BalanceSettlementService.getBalanceSettlementPdfByIdAsync(
      { accountId, balanceSettlementId },
      { responseType: 'blob' }
    );
    promise.then((response: Blob) => {
      const receiptName = this.rootStore.contentStore.cmsStore.getContent(
        'BalanceSettlementReceipt'
      )?.content;
      saveAs(response, `${receiptName ?? 'receipt'}.pdf`);
    });
    return promise;
  };

  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this, {
      balance: observable,
      shouldShowBalanceBanner: computed,
      settlementStatus: observable,
      balanceSettlementId: observable,
      settledAmount: observable,
      settledCurrency: observable,
      settledBalanceType: observable,
      isPolling: observable,
      getBalance: action,
      hasCreditBalance: action,
      createBalanceSettlement: action,
      processBalanceSettlement: action,
      cancelBalanceSettlement: action,
      getBalanceSettlementStatus: action,
      pollSettlementStatus: action,
      stopPolling: action,
      clearSettlement: action,
      initiateSettlement: action,
      getBalanceSettlement: action,
      getBalanceSettlementPdf: action
    });
  }
}
